import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Link2, Unlink } from 'lucide-react';
import { API_BASE } from '../../config/api';

function authHeaders(token) {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export default function WidgetCricketLinkPanel({ layoutId, authToken, widgetId, readOnly, onOverlayInvalidate }) {
  const { t } = useTranslation(['cricket', 'builder']);
  const [loading, setLoading] = useState(true);
  const [lists, setLists] = useState([]);
  const [cards, setCards] = useState([]);
  const [error, setError] = useState(null);
  const [listId, setListId] = useState('');
  const [cardTitle, setCardTitle] = useState('');
  const [chirpNew, setChirpNew] = useState('');
  const [chirpDraft, setChirpDraft] = useState('');
  const [linkedTitleDraft, setLinkedTitleDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!layoutId || !authToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) {
        setError('load');
        setLoading(false);
        return;
      }
      const data = await res.json();
      const sl = [...(data.lists || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setLists(sl);
      const allCards = data.cards || [];
      setCards(allCards);
      const linkedCard = allCards.find((c) => c.widget_id && String(c.widget_id) === String(widgetId));
      setCardTitle(linkedCard?.title || t('cricket:linkCardDefaultTitle', { id: String(widgetId || '').slice(0, 8) }));
      setListId((prev) => {
        if (prev && sl.some((l) => l.id === prev)) return prev;
        const todo = sl.find((l) => l.list_key === 'todo');
        return (todo || sl[0])?.id || '';
      });
    } catch {
      setError('load');
    } finally {
      setLoading(false);
    }
  }, [layoutId, authToken, widgetId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const linked = useMemo(() => (cards || []).find((c) => c.widget_id && String(c.widget_id) === String(widgetId)), [cards, widgetId]);

  useEffect(() => {
    setChirpDraft(linked?.body != null ? String(linked.body) : '');
  }, [linked?.id, linked?.body]);

  useEffect(() => {
    setLinkedTitleDraft(linked?.title != null ? String(linked.title) : '');
  }, [linked?.id, linked?.title]);

  const listTitleById = useMemo(() => Object.fromEntries((lists || []).map((l) => [l.id, l.title])), [lists]);

  const defaultTitle = t('cricket:linkCardDefaultTitle', { id: String(widgetId || '').slice(0, 8) });

  const handleCreate = async () => {
    if (readOnly || !authToken || !listId) return;
    const title = (cardTitle || defaultTitle).trim();
    if (!title) return;
    setBusy(true);
    try {
      const body = chirpNew.trim();
      const res = await fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/cards`, {
        method: 'POST',
        headers: authHeaders(authToken),
        body: JSON.stringify({
          list_id: listId,
          title,
          widget_id: widgetId,
          ...(body ? { body } : {}),
        }),
      });
      if (!res.ok) {
        setError('save');
        return;
      }
      await load();
      onOverlayInvalidate?.();
    } catch {
      setError('save');
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async () => {
    if (readOnly || !authToken || !linked) return;
    setBusy(true);
    try {
      const res = await fetch(
        `${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/cards/${encodeURIComponent(linked.id)}`,
        { method: 'PATCH', headers: authHeaders(authToken), body: JSON.stringify({ widget_id: null }) },
      );
      if (!res.ok) {
        setError('save');
        return;
      }
      await load();
      onOverlayInvalidate?.();
    } catch {
      setError('save');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveLinkedTitle = async () => {
    if (readOnly || !authToken || !linked) return;
    const title = linkedTitleDraft.trim();
    if (!title) return;
    setBusy(true);
    try {
      const res = await fetch(
        `${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/cards/${encodeURIComponent(linked.id)}`,
        { method: 'PATCH', headers: authHeaders(authToken), body: JSON.stringify({ title }) },
      );
      if (!res.ok) {
        setError('save');
        return;
      }
      await load();
      onOverlayInvalidate?.();
    } catch {
      setError('save');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveChirp = async () => {
    if (readOnly || !authToken || !linked) return;
    setBusy(true);
    try {
      const res = await fetch(
        `${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/cards/${encodeURIComponent(linked.id)}`,
        { method: 'PATCH', headers: authHeaders(authToken), body: JSON.stringify({ body: chirpDraft.trim() || '' }) },
      );
      if (!res.ok) {
        setError('save');
        return;
      }
      await load();
      onOverlayInvalidate?.();
    } catch {
      setError('save');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" aria-hidden />
      </div>
    );
  }

  if (error === 'load') {
    return <p className="text-sm text-red-600 dark:text-red-400">{t('cricket:loadError')}</p>;
  }

  return (
    <div className="space-y-4 p-1">
      <p className="text-sm text-slate-600 dark:text-slate-400">{t('cricket:linkIntro')}</p>

      {readOnly && <p className="text-xs text-amber-800 dark:text-amber-200">{t('cricket:readOnlyHint')}</p>}

      {linked ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/80 p-3 dark:border-emerald-900 dark:bg-emerald-950/40">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-emerald-900 dark:text-emerald-100">
            <Link2 size={16} aria-hidden />
            {t('cricket:linkedTitle')}
          </div>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            {t('cricket:linkedList', { name: listTitleById[linked.list_id] || '—' })}
          </p>
          <div className="bc-form-group mt-3">
            <label className="bc-label">{t('cricket:linkedCardTitleField')}</label>
            <input
              className="bc-input"
              value={linkedTitleDraft}
              onChange={(e) => setLinkedTitleDraft(e.target.value)}
              readOnly={readOnly}
              maxLength={500}
            />
            {!readOnly && (
              <button
                type="button"
                disabled={busy || !linkedTitleDraft.trim()}
                onClick={handleSaveLinkedTitle}
                className="mt-2 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
              >
                {busy ? '…' : t('cricket:saveLinkedCardTitle')}
              </button>
            )}
          </div>
          <div className="bc-form-group mt-3">
            <label className="bc-label">{t('cricket:chirpLabel')}</label>
            <textarea
              className="bc-input min-h-[88px] resize-y font-mono text-xs"
              value={chirpDraft}
              onChange={(e) => setChirpDraft(e.target.value)}
              readOnly={readOnly}
              placeholder={t('cricket:chirpPlaceholder')}
              maxLength={8000}
            />
            {!readOnly && (
              <button
                type="button"
                disabled={busy}
                onClick={handleSaveChirp}
                className="mt-2 w-full rounded-md border border-emerald-600 bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50 dark:border-emerald-500 dark:bg-emerald-700 dark:hover:bg-emerald-600"
              >
                {busy ? '…' : t('cricket:chirpSave')}
              </button>
            )}
          </div>
          {!readOnly && (
            <button
              type="button"
              disabled={busy}
              onClick={handleUnlink}
              className="mt-3 inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <Unlink size={14} />
              {t('cricket:unlink')}
            </button>
          )}
          {error === 'save' && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{t('cricket:linkSaveError')}</p>}
        </div>
      ) : (
        !readOnly && (
          <>
            <div className="bc-form-group">
              <label className="bc-label">{t('cricket:selectList')}</label>
              <select className="bc-input" value={listId} onChange={(e) => setListId(e.target.value)}>
                {lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="bc-form-group">
              <label className="bc-label">{t('cricket:cardTitleField')}</label>
              <input className="bc-input" value={cardTitle} onChange={(e) => setCardTitle(e.target.value)} placeholder={defaultTitle} />
            </div>
            <div className="bc-form-group">
              <label className="bc-label">{t('cricket:chirpLabel')}</label>
              <textarea
                className="bc-input min-h-[72px] resize-y font-mono text-xs"
                value={chirpNew}
                onChange={(e) => setChirpNew(e.target.value)}
                placeholder={t('cricket:chirpPlaceholder')}
                maxLength={8000}
              />
            </div>
            {error === 'save' && <p className="text-xs text-red-600">{t('cricket:linkSaveError')}</p>}
            <button
              type="button"
              disabled={busy || !listId}
              onClick={handleCreate}
              className="w-full rounded-lg bg-amber-600 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {busy ? '…' : t('cricket:createLink')}
            </button>
          </>
        )
      )}
    </div>
  );
}
